// @flow
import { endsWith } from "voca";
import appConfig from "appConfig";
import { consts as uploaderConsts } from "uploader";
import { getLogger } from "common/logger";
import { openWindow, localStorage } from "utils/domUtils";
import { api, common as servicesCommon, createTtlHandler } from "services";
import t from "common/i18n";
import consts from "../data/consts";
import type {
	SearchParams, MEDIA_TYPE, Query, SIZES, LicenceInfo,
} from "../data/types";

const logger = getLogger();
const serverBase = appConfig.shutterStock.baseUrl;
const { apiSubDomain } = appConfig.shutterStock;
const { authServer } = appConfig.shutterStock;
const { integration } = appConfig.shutterStock;
const { SHUTTERSTOCK } = uploaderConsts.SOURCES;

export const LS_INTEGRATION_KEY = "UW_Shutterstock_Integration";
const LS_API_DOMAIN_KEY = "UW_Shutterstock_Api";

let authWin = null;

type authInfoParams = { token: ?string, cloudName: string };

type Config = { cloudName: string, server: string, newTlsDomain?: boolean };

const tokenTtlHandler = createTtlHandler(consts.TOKEN_TTL_MINUTES);

const getAuthenticationUrl = () => {
	const integrationOverride = localStorage.getItem(LS_INTEGRATION_KEY);
	return `${authServer}${integrationOverride || integration}/`;
};

const getBaseUrl = () => {
	const subDomainOverride = localStorage.getItem(LS_API_DOMAIN_KEY);
	const subDomain = subDomainOverride || apiSubDomain;

	return `https://${subDomain}${serverBase}`;
};

const validateToken = (cloudName) => {
	const token = servicesCommon.getStoredAuthToken(cloudName, SHUTTERSTOCK);

	if (!token) {
		const err: Object = new Error("No token found");
		err.status = 401;
		throw err;
	}
};

const getRequestHeaders = (cloudName: string) => {
	const token = servicesCommon.getStoredAuthToken(cloudName, SHUTTERSTOCK);
	return token ? {
		"User-Agent": "request",
		Authorization: `Bearer ${token}`,
		"Content-Type": "application/json",
	} : {};
};

const getAuthInfo = (cloudName: string): { token: ?string, cloudName: string } => ({
	cloudName,
	token: servicesCommon.getStoredAuthToken(cloudName, SHUTTERSTOCK),
});

export default () => {
	const baseUrl = getBaseUrl();
	const authUrl = getAuthenticationUrl();

	const openAuthWindow = (config: Config) => {
		const redirectUrl = servicesCommon.getAuthRedirectUrl(config.newTlsDomain);

		const server = config.server || authUrl;

		let loginUrl = `${server}${endsWith(server, "/") ? "" : "/"}`;
		loginUrl = `${loginUrl}${config.cloudName}/login?redirect_url=${redirectUrl}`;

		logger.log(`[shutterStockService]: created Shutterstock login link: ${loginUrl}`);

		authWin = openWindow(loginUrl, "ShutterStockAuthWin");
	};

	const setAuthListener = (cloudName: string) => servicesCommon.setMessageAuthListener(() => authWin, cloudName, SHUTTERSTOCK)
		.then((result) => {
			authWin = null;
			return result;
		});

	const logout = (cloudName: string) => {
		servicesCommon.removeAuthToken(cloudName, SHUTTERSTOCK);
	};

	const getAccountInfo = async (params: authInfoParams) => {
		const headers = getRequestHeaders(params.cloudName);
		const accountInfo = {};

		let response = await api.get(`${baseUrl}user`, {}, { headers });

		accountInfo.user = response.id;
		accountInfo.name = response.full_name || response.username;

		response = await api.get(
			`${baseUrl}user/subscriptions`,
			{},
			{ headers },
		);

		response.data.forEach((plan) => {
			// Determine mediaType for each plan. Required for filtering irrelevant plans in the purchase page.
			plan.mediaType = plan.formats.find((format) => format.format === "jpg")
				? consts.MEDIA_TYPES.IMAGES
				: consts.MEDIA_TYPES.VIDEOS;

			plan.valid = new Date(plan.expiration_time).getTime() > new Date().getTime()
                && (plan.allotment && plan.allotment.downloads_left > 0);
		});

		accountInfo.subscriptions = response.data;

		return accountInfo;
	};

	/**
     * See available query params here:
     * https://api-reference.shutterstock.com/#images-search-for-images
     * */
	const getQueryForParams = (params: SearchParams, nextPage: number): Query => {
		const { filters } = params;

		let query: Query = {
			query: params.searchTerm,
			page: nextPage,
			view: "full",
			per_page: params.maxResults,
			sort: params.sort,
			safe: filters.safe,
			people_gender: filters.withPeopleOnly ? filters.gender : undefined,
			people_number: filters.withPeopleOnly ? filters.numberOfPeople : undefined,
			category: filters.category !== "all" ? filters.category : undefined,
		};

		if (params.mediaType === consts.MEDIA_TYPES.IMAGES) {
			query = {
				...query,
				orientation: filters.orientation || undefined,
				color: filters.color || undefined,
				image_type: filters.type !== "all" ? filters.type : undefined,
			};
		} else if (params.mediaType === consts.MEDIA_TYPES.VIDEOS) {
			query = {
				...query,
				duration_to: filters.duration === "short" ? consts.VIDEO_DURATION.SHORT : undefined,
				duration_from: filters.duration === "long" ? consts.VIDEO_DURATION.LONG : undefined,
				resolution: filters.resolution || undefined,
			};
		}

		return query;
	};

	const getSearchResults = async (cloudName: string, params: SearchParams, nextPage: number) => {
		validateToken(cloudName);

		const mediaType: MEDIA_TYPE = params.mediaType || consts.MEDIA_TYPES.IMAGES;
		const query = getQueryForParams(params, nextPage);

		const response = await api.get(`${baseUrl}${mediaType}/search`,
			query,
			{ headers: getRequestHeaders(cloudName) });

		const isVideo = mediaType === consts.MEDIA_TYPES.VIDEOS;

		const results = response.data.map((item) => ({
			id: item.id,
			link: isVideo ? item.assets.preview_mp4.url : item.assets.preview_1500.url,
			preview: {
				height: isVideo ? item.assets.preview_mp4.height : item.assets.preview_1500.height,
				width: isVideo ? item.assets.preview_mp4.width : item.assets.preview_1500.width,
			},
			thumbnail: isVideo ? item.assets.thumb_mp4.url : item.assets.huge_thumb.url,
			title: "",
			subtitle: item.description,
			duration: item.duration,
			searchId: response.search_id,
			assets: item.assets,
			contributorId: item.contributor.id,
			contributor: {},
			aspect: item.aspect,
		}));

		return {
			results,
			total: response.total_count,
			page: response.page,
		};
	};

	const getImageById = async (cloudName: string, mediaType: MEDIA_TYPE, id: string) => {
		validateToken(cloudName);

		const response = await api.get(
			`${baseUrl}${mediaType}/${id}`,
			{},
			{ headers: getRequestHeaders(cloudName) },
		);

		const result = {
			id: response.id,
			link: mediaType === "videos" ? response.assets.preview_mp4.url : response.assets.preview_1500.url,
			thumbnail: mediaType === "videos" ? response.assets.thumb_mp4.url : response.assets.huge_thumb.url,
			title: "",
			subtitle: response.description,
			duration: response.duration,
			searchId: response.search_id,
			assets: response.assets,
			contributorId: response.contributor.id,
			contributor: {},
			aspect: response.aspect,
		};

		return {
			results: [result],
			total: 1,
			page: 1,
		};
	};

	const getContributors = async (cloudName: string, idCollection: Array<string>) => {
		const response = await api.get(
			`${baseUrl}contributors`,
			{ id: idCollection },
			{ headers: getRequestHeaders(cloudName) },
		);

		return response.data ? response.data : [];
	};

	const getCategories = async (cloudName: string): Promise<{ label: string, value: string }[]> => {
		validateToken(cloudName);

		const response = await api.get(
			`${baseUrl}images/categories`, {},
			{ headers: getRequestHeaders(cloudName) },
		);

		return [{
			label: t("shutterstock.filters.all_categories"),
			value: "all",
		}].concat(response.data
			.map((cat) => ({
				label: cat.name,
				value: cat.id,
			})));
	};

	const setAssetsLicence = async (
		cloudName: string,
		mediaType: string,
		assets: Array<LicenceInfo>,
	) => {
		validateToken(cloudName);

		const baseUrl = getBaseUrl();

		return await api.post(`${baseUrl}${mediaType}/licenses`, {
			[mediaType]: assets,
		}, { headers: getRequestHeaders(cloudName) });
	};

	const download = async (
		cloudName: string,
		mediaType: MEDIA_TYPE,
		size: SIZES,
		licenceId: string,
	) => {
		validateToken(cloudName);

		return await api.post(`${baseUrl}${mediaType}/licenses/${licenceId}/downloads`, {
			size,
		}, { headers: getRequestHeaders(cloudName) });
	};

	// const getLicencedMedia = async (cloudName: string, category: "videos" | "images") => {
	//     validateToken(cloudName);
	//     const response = await api.get(`${baseUrl}${category}/licenses`, {}, { headers: getRequestHeaders(cloudName) });
	//     return response;
	// };

	const getLicenceInfoForAsset = async (cloudName: string, mediaType: MEDIA_TYPE, assetId: string) => {
		validateToken(cloudName);

		const params = mediaType === "images" ? { image_id: assetId } : { video_id: assetId };

		const response = await api.get(`${baseUrl}${mediaType}/licenses`,
			params,
			{ headers: getRequestHeaders(cloudName) });
		return response.data ? response.data[0] : null;
	};

	const retrieveAssetPurchaseData = async (item: Object, cloudName: string, mediaType: MEDIA_TYPE) => {
		validateToken(cloudName);

		const contributorPromise = getContributors(cloudName, [item.contributorId])
			.catch((err) => {
				logger.log(`[shutterStockService]: failed to retrieve contributor info for ${item.contributorId}`, err);
				return null;
			});

		const licenceInfoPromise = getLicenceInfoForAsset(cloudName, mediaType, item.id);

		const results = await Promise.all([
			contributorPromise,
			licenceInfoPromise,
		]);

		return {
			id: item.id,
			contributor: results[0] ? results[0][0] : [],
			licenceInfo: results[1],
		};
	};

	const getTtlHandler = () => tokenTtlHandler;

	return {
		getAuthInfo,
		openAuthWindow,
		setAuthListener,
		logout,
		getAccountInfo,
		getSearchResults,
		getCategories,
		setAssetsLicence,
		getImageById,
		download,
		retrieveAssetPurchaseData,
		getTtlHandler,
	};
};
